/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import styled from '@emotion/styled';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Button } from '@blueprintjs/core';
import { WorkspaceContext } from '/views/workspace/workspaceContext';
import { FacetSearch } from '../facetSearch';
import { useFacetsState } from 'hooks/useFacetsState';
import { selectDilDefinitionByName } from 'srs-middleware';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { hierarchicalFacets } from 'widgets/endpoints';
import { DividerFull, FlexSplitter } from 'styles/styledComponentUtils';
import { FacetHeader } from 'components/facets/facetHeader';
import { SORT_TYPES, SortSelect } from '../../../components/sortSelect';
import { Sizes } from 'styles/variables-slykit';
import { ScrollBox } from 'components/scrollBox';
import { HierarchicalFacetDetail } from './hierarchicalFacetDetail';
import { ExcludedFacetsDisplay } from '../../../components/facets/facetItem';
import { EmptyFacetsMessage } from 'components/facets/emptyFacetsMessage';
import { ExpandContext, useExpandCollapse } from 'views/filterHub/hierarchicalFacet/expandedStateManager';

/**
 * HierarchicalFacetPanel displays all of the hierarchical facets.
 *
 * @param {function} closePanel passed down from blueprint PanelStack component. Closes panel and reopens the HierarchicalFacetsPreview
 * @param {DILProperty} property indicates the specific property to render
 */
export const HierarchicalFacetPanel = ({ closePanel, property }) => {
	const [filters, setFilters] = useState(null);
	const [sort, setSort] = useState(SORT_TYPES.NUMERIC.label);

	const {
		state: {
			explorer: { selectedDefinitionName },
			transient: { activeResult, rootResult },
		},
	} = useContext(WorkspaceContext);
	const { updateHierarchicalFacets, getActiveHierarchicalFacets, getTextFilter, toggleHierarchicalFacet } =
		useFacetsState();
	const textFilter = getTextFilter();

	const selectedDefinition = useMemo(() => {
		return selectDilDefinitionByName(selectedDefinitionName);
	}, [selectedDefinitionName]);

	// memoize the parameters, so we don't keep fetching on rerender
	const parameters = useMemo(() => {
		return {
			textFilter: textFilter,
			sort: sort === SORT_TYPES.NUMERIC.label ? SORT_TYPES.NUMERIC.value : SORT_TYPES.ALPHABETICAL.value,
			parentResultId: rootResult.resultId
		};
	}, [textFilter, sort, rootResult.resultId]);

	useEffect(() => {
		if (rootResult === null) {
			closePanel();
		}
	}, [rootResult, closePanel]);

	// fetch the data, given the current parameters
	const { data, loading } = useAsyncCallback(async () => {
		if (activeResult && property && selectedDefinition) {
			if (activeResult.count > 0) {
				const response = await hierarchicalFacets(activeResult, selectedDefinitionName, {
					propertyList: [property.name],
					parameters: parameters,
				});

				const filters = response[selectedDefinition.systemDefinitionId]?.[property.name];
				return { filters };
			}
		}
	}, [activeResult, property, selectedDefinition, parameters]);

	// pull out the filters from the return value of the async callback
	// need to keep using the old filters until we new filters so the screen doesn't blink to blank during loading
	useEffect(() => {
		if (!loading) {
			// SRS keys are case insensitive which can cause some problems, transform key to lowercase
			if (data?.filters) {
				data.filters.entries.forEach((entry) => {
					entry.label = entry.key;
					entry.key = entry.key.toLowerCase();
				});
				setFilters(data.filters);
			}
		}
	}, [data, loading]);

	const { isAnyExpanded, contextValue, toggleAll } = useExpandCollapse(filters?.entries);

	const resetHierarchalFacets = () => {
		updateHierarchicalFacets(property.name, []);
	};

	return (
		<Wrapper>
			<section>
				<FacetHeader
					title={property.name}
					loading={loading}
					onResetClick={resetHierarchalFacets}
					onBackClick={closePanel}
					css={css`
						margin: ${Sizes.md} ${Sizes.lg} ${Sizes.sm};
					`}
				/>

				<DividerFull />

				<div css={optionsRow}>
					<Button
						icon={isAnyExpanded ? 'collapse-all' : 'expand-all'}
						onClick={toggleAll}
						minimal
						small
						css={css`
							margin-left: -4px;
						`}
					/>

					<SortSelect
						sortOptions={[SORT_TYPES.NUMERIC.label, SORT_TYPES.ALPHABETICAL.label]}
						selectedSortOption={sort}
						setSelectedSortOption={setSort}
					/>

					<FlexSplitter />

					<FacetSearch findIn={property.name} />
				</div>

				<DividerFull />
			</section>

			<ExcludedFacetsDisplay
				excludedFacets={getActiveHierarchicalFacets()}
				toggleFacet={toggleHierarchicalFacet}
				property={property.name}
			/>

			<ExpandContext.Provider value={contextValue}>
				<ScrollBox>
					{filters?.entries?.map((item) => {
						return <HierarchicalFacetDetail key={item.key} facet={item} property={property} />;
					})}
					{filters?.entries?.length === 0 && <EmptyFacetsMessage />}
				</ScrollBox>
			</ExpandContext.Provider>
		</Wrapper>
	);
};

const optionsRow = css`
	display: flex;
	align-items: center;
	margin: ${Sizes.xxsm} ${Sizes.lg};
`;

const Wrapper = styled.div`
	width: 100%;
	height: 100%;
	overflow: hidden;
	display: flex;
	flex-direction: column;
`;
